import { useState, useMemo, useEffect, useCallback } from 'react';
import { Trash2, Plus, Download, Upload, Sparkles, ClipboardList, AlertTriangle, HelpCircle, CheckCircle2, ShieldAlert, Search, ChevronDown, MoreHorizontal } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter, AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel } from '@/components/ui/alert-dialog';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { useApp } from '@/contexts/AppContext';
import { useQuestions } from '@/contexts/QuestionsContext';
import { useAuth } from '@/contexts/AuthContext';
import { useProject } from '@/contexts/ProjectContext';
import { useDiagramData } from '@/hooks/useDiagramData';
import { useQuestionsGeneration } from '@/hooks/useQuestionsGeneration';
import { useThreatCatSend } from '@/hooks/useThreatCatSend';
import { useQuestionsActions } from '@/hooks/useQuestionsActions';
import { useVulnerabilitiesQuery } from '@/hooks/useVulnerabilitiesQuery';
import { questionsService } from '@/services/questionsService';
import type { QuestionOut } from '@/services/questionsService';
import { getQuestionStatus } from '@/lib/questionUtils';
import { parsePhase, PHASES } from '@/lib/phaseUtils';
import ContextHintsModal from './ContextHintsModal';
import QuestionRow from './questions/QuestionRow';
import ConnectModal from '@/components/threatcat/ConnectModal';

export default function QuestionsTab() {
  const { diagrams, isProject } = useApp();
  const { questions, quickScanResults, projectId: qProjectId, loadQuestions, isGeneratingQuestions: isGenerating, isAnalyzingQuestionnaire: isAnalyzing, analyzingQuestionIds } = useQuestions();
  const { user } = useAuth();
  const { projectId: selectedProjectId } = useProject();
  const { currentDiagram } = useDiagramData();

  const currentProjectId = qProjectId ?? selectedProjectId ?? 0;

  // ── Extracted hooks ─────────────────────────────────────────────────────────
  const { state: sendState, actions: sendActions } = useThreatCatSend();
  const {
    handleAdd: doAdd,
    handleDelete,
    handleClearAll,
    handleUpdateQuestion,
    handleUpdateAnswer,
    handleUpdateNotes,
    handleUpdateFinding,
    handleMarkStatus,
    handleAnalyzeItem: doAnalyzeItem,
    handleLinkVulnerability,
    handleUnlinkVulnerability,
  } = useQuestionsActions(currentProjectId);

  const [selectedDiagramIndices, setSelectedDiagramIndices] = useState<Set<number>>(
    () => new Set((diagrams ?? []).map((_, i) => i))
  );

  const toggleDiagram = (index: number, checked: boolean) => {
    setSelectedDiagramIndices((prev) => {
      const next = new Set(prev);
      if (checked) next.add(index); else next.delete(index);
      return next;
    });
  };

  const canGenerate = user?.roles?.includes('especial');

  // ── Generation / parse / analyze (extracted hook) ─────────────────────────
  const { state: genState, actions: genActions } = useQuestionsGeneration(currentProjectId, selectedDiagramIndices);
  const { generateError, isParsing, parseError, analyzeError, fileInputRef } = genState;
  const { handleGenerateQuestions, handleAnalyze, handleFileInput, setAnalyzeError } = genActions;

  // ── Add-question form state ─────────────────────────────────────────────────
  const [newQuestion, setNewQuestion] = useState('');
  const [newDomain, setNewDomain] = useState('');
  const [newAsrgIds, setNewAsrgIds] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);

  // ── Context hints modal state ───────────────────────────────────────────────
  const [contextModalOpen, setContextModalOpen] = useState(false);
  const [isaContextDraft, setIsaContextDraft] = useState('');

  // ── Clear All confirmation state ────────────────────────────────────────────
  const [clearAllDialogOpen, setClearAllDialogOpen] = useState(false);

  // ── Project vulnerabilities (for linking combobox) ────────────────────────
  const { data: projectVulnerabilities = [] } = useVulnerabilitiesQuery(currentProjectId || null);

  // Load questions when project is available (sets projectId in context, enabling the query)
  useEffect(() => {
    if (currentProjectId) {
      loadQuestions(currentProjectId);
    }
  }, [currentProjectId, loadQuestions]);

  // ── Search, filter & UI state ─────────────────────────────────────────────
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [phaseFilter, setPhaseFilter] = useState<string>('all');
  const [collapsedDomains, setCollapsedDomains] = useState<Set<string>>(new Set());

  const toggleDomainCollapse = (domain: string) => {
    setCollapsedDomains((prev) => {
      const next = new Set(prev);
      if (next.has(domain)) next.delete(domain); else next.add(domain);
      return next;
    });
  };

  // ── Handlers (remaining thin wrappers that touch local UI state) ───────────
  const handleAnalyzeItem = (question: QuestionOut) => doAnalyzeItem(question, setAnalyzeError);

  const handleAdd = async () => {
    await doAdd(newQuestion, newDomain, newAsrgIds);
    setNewQuestion('');
    setNewDomain('');
    setNewAsrgIds('');
    setShowAddForm(false);
  };

  const handleExportQuestionnaire = useCallback(async () => {
    if (questions.length === 0 || !currentProjectId) return;
    try {
      await questionsService.exportQuestionnaireXlsx(currentProjectId);
    } catch (err) {
      console.error('export questionnaire xlsx error:', err);
    }
  }, [questions.length, currentProjectId]);

  // Destructure send state for cleaner JSX
  const { sendingQuestionIds, sendResults, sendErrors, connectionOpen, writeBackLockMessage } = sendState;
  const { handleSendToThreatCat, setConnectionOpen, onConnected } = sendActions;

  // ── Derived data ─────────────────────────────────────────────────────────────
  const hasAnswers = questions.some((q) => Boolean(q.answer?.trim()));
  const answeredCount = questions.filter((q) => Boolean(q.answer?.trim())).length;
  const analysisCount = questions.filter((q) => 
    q.vulnerabilities?.length || quickScanResults[q.id] || (q.manual_status && q.manual_status.startsWith('quick_scan_'))
  ).length;
  const hasData = hasAnswers || analysisCount > 0;
  // Questions eligible for Quick Scan: answered, unclassified, no finding (mirrors backend filter)
  const eligibleQuickScanCount = questions.filter(
    (q) => Boolean(q.answer?.trim()) && !q.manual_status && !q.vulnerabilities?.length,
  ).length;

  // Global counts (unfiltered)
  let vulnerableCount = 0, clarificationCount = 0, okCount = 0, pendingCount = 0;
  for (const q of questions) {
    const s = getQuestionStatus(q, quickScanResults);
    if (s === 'vulnerable') vulnerableCount++;
    else if (s === 'clarification') clarificationCount++;
    else if (s === 'ok') okCount++;
    else pendingCount++;
  }

  // Filter questions
  const filteredQuestions = useMemo(() => {
    let result = questions;
    if (searchQuery.trim()) {
      const lq = searchQuery.toLowerCase();
      result = result.filter(
        (q) => q.question.toLowerCase().includes(lq) || (q.answer ?? '').toLowerCase().includes(lq) || q.security_domain.toLowerCase().includes(lq) || (q.external_id ?? '').toLowerCase().includes(lq)
      );
    }
    if (statusFilter !== 'all') {
      result = result.filter((q) => getQuestionStatus(q, quickScanResults) === statusFilter);
    }
    if (phaseFilter !== 'all') {
      result = result.filter((q) => parsePhase(q.external_id) === phaseFilter);
    }
    return result;
  }, [questions, searchQuery, statusFilter, phaseFilter, quickScanResults]);

  // Group filtered questions by domain
  const grouped: Record<string, QuestionOut[]> = {};
  for (const q of filteredQuestions) {
    const domain = q.security_domain || 'General';
    if (!grouped[domain]) grouped[domain] = [];
    grouped[domain].push(q);
  }
  const domains = Object.keys(grouped).sort(
    (a, b) => Math.min(...grouped[a].map((q) => q.id)) - Math.min(...grouped[b].map((q) => q.id))
  );

  // Per-domain summaries
  const domainSummary = (domainQuestions: QuestionOut[]) => {
    let ok = 0, vuln = 0, clar = 0, pend = 0;
    for (const q of domainQuestions) {
      const s = getQuestionStatus(q, quickScanResults);
      if (s === 'ok') ok++;
      else if (s === 'vulnerable') vuln++;
      else if (s === 'clarification') clar++;
      else pend++;
    }
    return { ok, vuln, clar, pend };
  };

  // Phase detection for stepper
  const phase: 1 | 2 | 3 = analysisCount > 0 ? 3 : hasAnswers ? 2 : 1;

  return (
    <div className="space-y-4">
      {/* ── Header card ─────────────────────────────────────────────────────── */}
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between gap-4">
            <div>
              <CardTitle className="flex items-center gap-2">
                <ClipboardList className="h-5 w-5" />
                Security Questions
              </CardTitle>
              {questions.length > 0 && (
                <CardDescription className="mt-1">
                  {questions.length} question{questions.length !== 1 ? 's' : ''} across{' '}
                  {new Set(questions.map((q) => q.security_domain || 'General')).size} domain{new Set(questions.map((q) => q.security_domain || 'General')).size !== 1 ? 's' : ''}
                </CardDescription>
              )}
            </div>
            <div className="flex items-center gap-2 flex-wrap justify-end">
              {/* ── Primary action — context-aware ──────────────────────── */}
              {!hasData && canGenerate && (
                <Button
                  size="sm"
                  onClick={() => setContextModalOpen(true)}
                  disabled={isGenerating || (!isProject && !currentDiagram) || (isProject && diagrams.length > 1 && selectedDiagramIndices.size === 0)}
                >
                  <Sparkles className="mr-2 h-4 w-4" />
                  {isGenerating ? 'Generating…' : 'Generate Questions'}
                </Button>
              )}
              {hasData && canGenerate && hasAnswers && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span tabIndex={eligibleQuickScanCount === 0 ? 0 : undefined}>
                      <Button
                        size="sm"
                        onClick={handleAnalyze}
                        disabled={isAnalyzing || !currentDiagram || eligibleQuickScanCount === 0}
                      >
                        <Sparkles className="mr-2 h-4 w-4" />
                        {isAnalyzing ? 'Scanning…' : 'Quick Scan'}
                      </Button>
                    </span>
                  </TooltipTrigger>
                  {eligibleQuickScanCount === 0 && (
                    <TooltipContent>No answered questions left to classify</TooltipContent>
                  )}
                </Tooltip>
              )}

              {/* ── Secondary actions ──────────────────────────────────── */}
              <Button variant="outline" size="sm" onClick={() => setShowAddForm((v) => !v)}>
                <Plus className="mr-2 h-4 w-4" />
                <span className="hidden sm:inline">Add Question</span>
                <span className="sm:hidden">Add</span>
              </Button>

              {/* ── Overflow menu ──────────────────────────────────────── */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className="px-2">
                    <MoreHorizontal className="h-4 w-4" />
                    <span className="sr-only">More actions</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-52">
                  {canGenerate && (
                    <>
                      <DropdownMenuItem onClick={() => fileInputRef.current?.click()} disabled={isParsing || isAnalyzing}>
                        <Upload className="mr-2 h-4 w-4" />
                        {isParsing ? 'Parsing…' : 'Upload Questionnaire (.xlsx)'}
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                    </>
                  )}
                  <DropdownMenuItem onClick={handleExportQuestionnaire} disabled={questions.length === 0}>
                    <Download className="mr-2 h-4 w-4" />
                    Export Questionnaire (.xlsx)
                  </DropdownMenuItem>
                  {questions.length > 0 && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onSelect={() => setClearAllDialogOpen(true)} className="text-destructive focus:text-destructive">
                        <Trash2 className="mr-2 h-4 w-4" />
                        Clear All
                      </DropdownMenuItem>
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Hidden file input */}
              <input ref={fileInputRef} type="file" accept=".xlsx" className="hidden" onChange={handleFileInput} />
            </div>
          </div>

          {/* ── Phase stepper ──────────────────────────────────────────────── */}
          {questions.length > 0 && (
            <div className="mt-3 pt-3 border-t">
              <div className="flex items-center gap-1 text-xs">
                <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 font-medium transition-colors ${phase >= 1 ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}`}>
                  1. Load
                </span>
                <ChevronDown className="h-3 w-3 -rotate-90 text-muted-foreground" />
                <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 font-medium transition-colors ${phase >= 2 ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}`}>
                  2. Answer
                </span>
                <ChevronDown className="h-3 w-3 -rotate-90 text-muted-foreground" />
                <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 font-medium transition-colors ${phase >= 3 ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}`}>
                  3. Analyze
                </span>
                <span className="ml-2 text-muted-foreground">
                  {answeredCount}/{questions.length} answered ({questions.length > 0 ? Math.round((answeredCount / questions.length) * 100) : 0}%)
                </span>
              </div>
            </div>
          )}

          {generateError && <p className="mt-2 text-sm text-destructive" role="alert">{generateError}</p>}
          {parseError && <p className="mt-2 text-sm text-destructive" role="alert">{parseError}</p>}

          {/* Diagram selection is now inside ContextHintsModal */}
        </CardHeader>

        {/* Add Question form */}
        {showAddForm && (
          <CardContent className="border-t pt-4">
            <div className="space-y-3">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div className="space-y-1">
                  <label className="text-xs font-medium text-muted-foreground">
                    Security Domain <span className="text-destructive">*</span>
                  </label>
                  <Input
                    placeholder="e.g. Secrets Management"
                    value={newDomain}
                    onChange={(e) => setNewDomain(e.target.value)}
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-medium text-muted-foreground">
                    ASRG IDs (comma-separated)
                  </label>
                  <Input
                    placeholder="e.g. V2.10.3, V1.4.1"
                    value={newAsrgIds}
                    onChange={(e) => setNewAsrgIds(e.target.value)}
                  />
                </div>
              </div>
              <div className="space-y-1">
                <label className="text-xs font-medium text-muted-foreground">
                  Question <span className="text-destructive">*</span>
                </label>
                <Textarea
                  placeholder="Enter the security question…"
                  value={newQuestion}
                  onChange={(e) => setNewQuestion(e.target.value)}
                  rows={2}
                />
              </div>
              <div className="flex gap-2">
                <Button size="sm" onClick={handleAdd} disabled={!newQuestion.trim() || !newDomain.trim()}>
                  Add
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setShowAddForm(false);
                    setNewQuestion('');
                    setNewDomain('');
                    setNewAsrgIds('');
                  }}
                >
                  Cancel
                </Button>
              </div>
            </div>
          </CardContent>
        )}
      </Card>

      {/* ── Error banner ──────────────────────────────────────────────────────── */}
      {analyzeError && (
        <div className="flex items-start gap-3 rounded-md border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive" role="alert">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span className="flex-1">{analyzeError}</span>
          <button onClick={() => setAnalyzeError(null)} className="ml-2 text-destructive/60 hover:text-destructive" aria-label="Dismiss">×</button>
        </div>
      )}

      {/* ── Progress bar + summary ─────────────────────────────────────────── */}
      {questions.length > 0 && (
        <Card>
          <CardContent className="py-4 space-y-3">
            {/* Segmented progress bar */}
            <div className="h-2.5 w-full rounded-full bg-muted overflow-hidden flex" role="progressbar" aria-label="Question analysis progress">
              {okCount > 0 && (
                <div className="bg-green-500 transition-all duration-300" style={{ width: `${(okCount / questions.length) * 100}%` }} />
              )}
              {vulnerableCount > 0 && (
                <div className="bg-red-500 transition-all duration-300" style={{ width: `${(vulnerableCount / questions.length) * 100}%` }} />
              )}
              {clarificationCount > 0 && (
                <div className="bg-amber-500 transition-all duration-300" style={{ width: `${(clarificationCount / questions.length) * 100}%` }} />
              )}
            </div>
            {/* Counters */}
            <div className="flex flex-wrap gap-x-5 gap-y-1 text-xs">
              <span className="flex items-center gap-1.5 text-green-600 dark:text-green-400 font-medium">
                <CheckCircle2 className="h-3.5 w-3.5" />
                {okCount} OK
              </span>
              <span className="flex items-center gap-1.5 text-destructive font-medium">
                <ShieldAlert className="h-3.5 w-3.5" />
                {vulnerableCount} vulnerable
              </span>
              <span className="flex items-center gap-1.5 text-amber-600 dark:text-amber-400 font-medium">
                <HelpCircle className="h-3.5 w-3.5" />
                {clarificationCount} clarification
              </span>
              <span className="flex items-center gap-1.5 text-muted-foreground font-medium">
                <ClipboardList className="h-3.5 w-3.5" />
                {pendingCount} pending
              </span>
              <span className="ml-auto text-muted-foreground">
                {analysisCount} analyzed of {answeredCount} answered
              </span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ── Search + status filter ────────────────────────────────────────── */}
      {questions.length > 0 && (
        <div className="space-y-2">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search questions, answers, domains, IDs…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 h-9"
              />
            </div>
            <Select value={phaseFilter} onValueChange={setPhaseFilter}>
              <SelectTrigger className="h-9 w-full sm:w-44">
                <SelectValue placeholder="All phases" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All phases</SelectItem>
                {PHASES.map((p) => (
                  <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                ))}
                <SelectItem value="OTHER">Other</SelectItem>
                <SelectItem value="NONE">Uncategorized</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <ToggleGroup
            type="single"
            value={statusFilter}
            onValueChange={(v) => setStatusFilter(v || 'all')}
            className="justify-start"
          >
            <ToggleGroupItem value="all" size="sm" className="text-xs px-3">
              All
            </ToggleGroupItem>
            <ToggleGroupItem value="pending" size="sm" className="text-xs px-3">
              Pending
            </ToggleGroupItem>
            <ToggleGroupItem value="ok" size="sm" className="text-xs px-3">
              OK
            </ToggleGroupItem>
            <ToggleGroupItem value="vulnerable" size="sm" className="text-xs px-3">
              Vulnerable
            </ToggleGroupItem>
            <ToggleGroupItem value="clarification" size="sm" className="text-xs px-3">
              Clarification
            </ToggleGroupItem>
          </ToggleGroup>
        </div>
      )}

      {/* Filtered results count */}
      {questions.length > 0 && (searchQuery.trim() || statusFilter !== 'all' || phaseFilter !== 'all') && (
        <p className="text-xs text-muted-foreground">
          Showing {filteredQuestions.length} of {questions.length} questions
          {searchQuery.trim() && <> matching &ldquo;{searchQuery.trim()}&rdquo;</>}
        </p>
      )}

      {/* ── Empty state ──────────────────────────────────────────────────────── */}
      {questions.length === 0 && (
        <Card>
          <CardContent className="py-16 text-center">
            <ClipboardList className="mx-auto h-12 w-12 text-muted-foreground/30 mb-4" />
            <p className="text-lg font-medium text-muted-foreground">No security questions yet</p>
            <p className="text-sm text-muted-foreground mt-1 mb-6 max-w-md mx-auto">
              {canGenerate
                ? 'Get started by generating questions from your diagram or uploading an existing questionnaire.'
                : 'You need the AI_ENABLED entitlement to generate security questions.'}
            </p>
            {canGenerate && (
              <div className="flex items-center justify-center gap-3">
                <Button
                  onClick={() => setContextModalOpen(true)}
                  disabled={isGenerating || (!isProject && !currentDiagram)}
                >
                  <Sparkles className="mr-2 h-4 w-4" />
                  Generate from Diagram
                </Button>
                <Button variant="outline" onClick={() => fileInputRef.current?.click()} disabled={isParsing}>
                  <Upload className="mr-2 h-4 w-4" />
                  Upload Questionnaire
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* ── No filter results ─────────────────────────────────────────────── */}
      {questions.length > 0 && filteredQuestions.length === 0 && (
        <Card>
          <CardContent className="py-8 text-center">
            <Search className="mx-auto h-8 w-8 text-muted-foreground/30 mb-3" />
            <p className="text-sm font-medium text-muted-foreground">No questions match your filters</p>
            <button
              onClick={() => { setSearchQuery(''); setStatusFilter('all'); }}
              className="text-xs text-primary hover:text-primary/80 mt-1"
            >
              Clear filters
            </button>
          </CardContent>
        </Card>
      )}

      {/* ── Questions grouped by domain (collapsible) ────────────────────── */}
      {domains.map((domain) => {
        const dqs = grouped[domain];
        const summary = domainSummary(dqs);
        const isCollapsed = collapsedDomains.has(domain);

        return (
          <Card key={domain}>
            <Collapsible open={!isCollapsed} onOpenChange={() => toggleDomainCollapse(domain)}>
              <CardHeader className="pb-3">
                <CollapsibleTrigger className="flex items-center justify-between w-full text-left group">
                  <div>
                    <CardTitle className="text-base group-hover:text-primary transition-colors">{domain}</CardTitle>
                    <CardDescription className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5">
                      <span>{dqs.length} question{dqs.length !== 1 ? 's' : ''}</span>
                      {summary.ok > 0 && <span className="text-green-600 dark:text-green-400">{summary.ok} OK</span>}
                      {summary.vuln > 0 && <span className="text-destructive">{summary.vuln} vuln</span>}
                      {summary.clar > 0 && <span className="text-amber-600 dark:text-amber-400">{summary.clar} clar</span>}
                      {summary.pend > 0 && <span>{summary.pend} pending</span>}
                    </CardDescription>
                  </div>
                  <ChevronDown className={`h-4 w-4 text-muted-foreground shrink-0 transition-transform duration-200 ${isCollapsed ? '-rotate-90' : ''}`} />
                </CollapsibleTrigger>
              </CardHeader>
              <CollapsibleContent>
                <CardContent className="pt-0">
                  <div className="divide-y">
                    {dqs.map((q) => {
                      const isItemAnalyzing = analyzingQuestionIds.has(q.id);
                      return (
                        <QuestionRow
                          key={q.id}
                          question={q}
                          quickScanResult={quickScanResults[q.id]}
                          isItemAnalyzing={isItemAnalyzing}
                          canAnalyze={!!canGenerate && !!(q.answer?.trim())}
                          onDelete={handleDelete}
                          onAnalyze={handleAnalyzeItem}
                          onMarkStatus={handleMarkStatus}
                          onUpdateFinding={handleUpdateFinding}
                          onUpdateQuestion={handleUpdateQuestion}
                          onUpdateAnswer={handleUpdateAnswer}
                          onUpdateNotes={handleUpdateNotes}
                          onSendToThreatCat={handleSendToThreatCat}
                          isSending={sendingQuestionIds.has(q.id)}
                          sendResult={sendResults[q.id] ?? null}
                          sendError={sendErrors[q.id]}
                          sendDisabledReason={writeBackLockMessage}
                          projectVulnerabilities={projectVulnerabilities}
                          onLinkVulnerability={handleLinkVulnerability}
                          onUnlinkVulnerability={handleUnlinkVulnerability}
                        />
                      );
                    })}
                  </div>
                </CardContent>
              </CollapsibleContent>
            </Collapsible>
          </Card>
        );
      })}

      <ConnectModal
        open={connectionOpen}
        onClose={() => setConnectionOpen(false)}
        onConnected={onConnected}
      />

      {/* ── Clear All confirmation ────────────────────────────────────────────── */}
      <AlertDialog open={clearAllDialogOpen} onOpenChange={setClearAllDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Clear all questions?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently deletes all {questions.length} question{questions.length !== 1 ? 's' : ''} for this project, along with their answers and any quick-scan results or linked findings. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => handleClearAll()}
            >
              Clear All
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <ContextHintsModal
        open={contextModalOpen}
        onClose={() => setContextModalOpen(false)}
        onGenerate={async (isaContext) => {
          setIsaContextDraft(isaContext);
          const ok = await handleGenerateQuestions(isaContext);
          if (ok) {
            setContextModalOpen(false);
          }
        }}
        isGenerating={isGenerating}
        error={generateError}
        initialContext={isaContextDraft}
        diagrams={isProject ? diagrams : undefined}
        selectedDiagramIndices={isProject ? selectedDiagramIndices : undefined}
        onToggleDiagram={isProject ? toggleDiagram : undefined}
      />
    </div>
  );
}
